// Cloud decks for the true-scale local scene.
//
// Two curved shells built on the same tangent-plane-to-sphere mapping the
// terrain uses, so they follow planet curvature and drop below the horizon at
// the right distance: a cumulus deck at ~2 km and a cirrus veil at ~9 km.
//
// Coverage is sampled from the same equirectangular cloud map the orbital view
// uses, addressed by latitude and longitude, so the weather you fly through is
// the weather you saw from orbit. That map is only about 5 km per pixel at the
// equator, which is far too coarse this close, so it supplies the large-scale
// placement only and FBM detail in real metres supplies the structure. This is
// the "preserve weather placement, add bounded detail" split, not a volumetric
// solution; proper raymarched clouds belong with the wider surface work.
//
// Passing through a deck is handled by fading its alpha out as the camera
// nears its altitude, so a shell never clips through the near plane as a hard
// edge.

import * as THREE from 'three';
import { siteBasis, dirAt } from './terrain.js';
import { earthWeatherDriftCPU } from '../shaders/earthweather.js';
import { AIR_DEPTH_GLSL } from './airdepth.js';

const TWO_PI = Math.PI * 2;

// The camera altitudes (m) the decks and the volumetric clouds fade out over,
// where the weather is handed to the layer on the terrain patch
// (orbitclouds.js). Exported so they share one band and cloud cover stays
// continuous across it. It starts where the volume has finished turning
// from the pad's fair weather into the tops seen from orbit (30 to 80 km:
// the map's cover in weatherReg, the tops' opacity and light in
// cloudshaders.js), so both sides of the band draw the same clouds with the
// same look; above it the shell owns the view.
export const DECK_HIGH_FADE = [80e3, 100e3];

const VERT = /* glsl */`
  varying vec2 vUv;
  varying vec3 vLocal;
  varying vec3 vRel;
  varying float vRim;
  varying float vDist;
  // The deck's own east and south axes in the local scene frame, for the
  // relief slope the fragment shader reads off the weather field.
  varying vec3 vEast;
  varying vec3 vSouth;
  varying vec3 vUpAx;
  attribute float aRim;
  void main() {
    vUv = uv;
    vLocal = position;
    vRim = aRim;
    vEast = (modelMatrix * vec4(1.0, 0.0, 0.0, 0.0)).xyz;
    vSouth = (modelMatrix * vec4(0.0, 0.0, 1.0, 0.0)).xyz;
    vUpAx = (modelMatrix * vec4(0.0, 1.0, 0.0, 0.0)).xyz;
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vRel = wp.xyz - cameraPosition;
    vDist = length(vRel);
    gl_Position = projectionMatrix * viewMatrix * wp;
  }
`;

const FRAG = /* glsl */`
  precision highp float;
  precision highp sampler3D;
  varying vec2 vUv;
  varying vec3 vLocal;
  varying vec3 vRel;
  varying float vRim;
  varying float vDist;
  varying vec3 vEast;
  varying vec3 vSouth;
  varying vec3 vUpAx;

  uniform sampler2D uMap;
  uniform vec3 uSunDir;        // in the deck's local frame
  uniform vec3 uSkyCol;
  uniform float uTime;
  uniform float uCamAlt;       // camera altitude above the body, metres
  uniform float uDeckAlt;      // this deck's altitude, metres
  uniform float uThickness;    // fade band around the deck, metres
  uniform float uCoverage;     // bias on the coverage threshold
  uniform float uDetail;       // metres per detail cell
  uniform float uOpacity;
  uniform float uDayLight;
  uniform float uCirrus;       // 0 cumulus, 1 cirrus
  uniform float uHighFade;     // fades the decks out above the atmosphere
  uniform vec3 uHazeCol;       // lit low-air colour the deck hazes into
  uniform float uHazeBeta;     // sea-level extinction, per metre
  ${AIR_DEPTH_GLSL}
  uniform vec2 uVolFade;       // distance band where the deck takes over from the volume (m)
  uniform float uVolAmount;    // 0: no volumetric layer, 1: the volume owns the near field
  // The weather field the volumetric clouds are built from (volclouds.js
  // shares these uniforms): with it the cumulus deck shows the same clouds
  // in the same places, so the volume can hand over to it with distance
  // and altitude without the sky rearranging itself.
  uniform float uVolWeather;
  uniform sampler3D uWeather;
  uniform vec3 uWeatherOff;
  uniform vec3 uTiles;
  uniform vec3 uUp;
  uniform float uShellH;
  uniform vec4 uCloud;
  uniform vec4 uCloud2;
  uniform mat3 uToSurf;
  uniform vec3 uWindT;
  uniform vec3 uWindB;
  uniform float uCamR;
  uniform vec2 uField;
  uniform float uPixelAngle;   // radians a pixel subtends
  uniform vec2 uMapUV;         // the camera's place on the orbital cloud map
  uniform float uMapShift;     // the map's wind drift at the patch (map U), so uMap reads where the globe does
  uniform vec3 uDuDp;
  uniform vec3 uDvDp;

  float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x),
               mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), f.x), f.y);
  }
  // Band-limited fbm. fw is how many noise cells one pixel covers at the
  // first octave; any octave finer than about half a pixel would alias into
  // speckle (from a few kilometres up the deck used to read as TV static), so
  // it fades to its mean instead.
  float fbm(vec2 p, float fw) {
    float a = 0.5, s = 0.0;
    for (int i = 0; i < 5; i++) {
      s += a * mix(0.5, vnoise(p), 1.0 - smoothstep(0.25, 0.6, fw));
      p *= 2.03; fw *= 2.03; a *= 0.5;
    }
    return s;
  }

  // How much cloud the coverage admits of the placement field at a point on
  // the weather shell, in the Earth-fixed frame. The march's own carve
  // (weatherReg in cloudshaders.js); front is the macro front at the point.
  float volCarve(vec3 pq, float front, float cov) {
    vec4 w = texture(uWeather, uWeatherOff + pq / uTiles.x);
    float place = clamp(w.r + (front - 0.55) * 1.5 - 0.15, 0.0, 1.0);
    float band = 1.0 - cov;
    return pow(smoothstep(band, min(band + 0.3, 1.02), place), 1.6);
  }

  // Cumulus cover from the volumetric clouds' weather: the same placement
  // field, coverage carve and orbital-map bias as their march, read on the
  // shell where their bases sit. thick carries how solid the mass is, for
  // the relief shading; where a whole cloud is below a pixel it gives way to
  // the cover it would average to. slope is the field's own gradient along
  // the deck's axes, which is what the relief shading needs.
  void volWeather(out float thick, out float a, out float sharpOut, out vec2 slope) {
    vec3 p = uToSurf * vRel;
    // A single shell at the deck's height would sit a few hundred metres off
    // the clouds it stands for, and from low angles that parallax shows as
    // every cloud's double. Slide along the view ray to the height the eye
    // actually meets: their tops from above, their bases from below.
    float hEye = mix(uShellH + 50.0, uShellH + 900.0, smoothstep(uShellH + 300.0, uShellH + 2500.0, uCamAlt));
    vec3 dir = normalize(p);
    float mu = dot(dir, uUp);
    p += dir * clamp((hEye - uDeckAlt) / (abs(mu) > 0.02 ? mu : sign(mu + 1e-6) * 0.02), -60000.0, 60000.0);
    vec3 pw = p - normalize(uUp * uCamR + p) * (hEye - uShellH);
    // The march reads the weather in one domain, compressed along the wind
    // (cloud streets) by the constant the camera's offset is folded with:
    // the front and the de-tile warp at the shell point, the placement moved
    // by that warp. The deck has to read it the same way or the two disagree.
    vec3 pf = pw + uWindT * (dot(pw, uWindT) * (uField.x - 1.0));
    vec4 w0 = textureLod(uWeather, uWeatherOff + pf / uTiles.x, 0.0);
    vec3 pp = pw + (uWindT * (w0.b - 0.5) + uWindB * (w0.a - 0.5)) * (1060.0 * 0.34);
    vec3 pq = pp + uWindT * (dot(pp, uWindT) * (uField.x - 1.0));
    // The regional cloudiness, addressed the way the march addresses it: the
    // camera's own place on the orbital map plus a first-order step to this
    // point. The deck's per-vertex uv is piecewise linear across its 2.5 km
    // cells, so reading the map through it put kinks in the coverage, and
    // where those crossed the alpha cutoff the deck's veil broke into hard
    // angular shards over the sea.
    // uMap is the raw cloud map, which the globe reads carried in the
    // weather's wind (earthweather.js), so it is read that far upwind.
    vec2 muv = uMapUV + vec2(dot(uDuDp, p), dot(uDvDp, p)) - vec2(uMapShift, 0.0);
    float m = textureLod(uMap, muv, 2.0).r;
    float reg = pow(m, 1.0 / 2.2);
    float cov = clamp(uCloud.x + (reg - 0.15) * uCloud2.x, 0.02, 1.2);
    // Near the ground the field keeps its own fair-weather cumulus wherever
    // the camera is, since the orbital map is 5 km a texel and says nothing
    // about the sky over one county. Climbing out it has to become the
    // planet's weather instead: by the height the orbit layer takes over,
    // the fraction the shell carves from the same map (the volume's march
    // does the same, cloudshaders.js weatherReg), and none where the map is
    // clear. The map's clear-sky floor lifted by the gamma above drew an even
    // field of puffs over clear sea.
    float toMap = smoothstep(30000.0, 80000.0, uCamAlt);
    cov = mix(cov, clamp(m * 2.4, 0.0, 1.05), toMap);
    float carve = volCarve(pq, w0.g, cov) * mix(1.0, smoothstep(0.0, 0.03, cov), toMap);
    // The ground one pixel spans here, from the distance and the angle the
    // deck is seen at. A screen derivative would be the obvious way to ask
    // and it is the wrong one: it is constant across a triangle, so the
    // choice below flipped between neighbours and drew the same shards.
    float inc = abs(dot(normalize(vRel), normalize(vUpAx)));
    inc = inc > 0.04 ? inc : 0.04;            // a comparison, so nothing odd survives it
    float e = vDist * uPixelAngle / inc;
    e = e > 90.0 ? (e < 40000.0 ? e : 40000.0) : 90.0;
    float texels = e / (uTiles.x / 128.0);
    float sharp = 1.0 - smoothstep(0.5, 2.0, texels);
    // The relief slope, from the field itself along the deck's own axes: a
    // pixel this far out spans a whole cloud, so there is no slope in the
    // image to read.
    vec3 eSurf = uToSurf * vEast, sSurf = uToSurf * vSouth;
    slope = vec2(volCarve(pq + eSurf * e, w0.g, cov) - carve, volCarve(pq + sSurf * e, w0.g, cov) - carve) * (900.0 / e);
    slope = vec2(slope.x == slope.x ? slope.x : 0.0, slope.y == slope.y ? slope.y : 0.0);
    // A soft-edged puff: seen from tens of kilometres a cumulus is a white
    // tuft, brightest over its thick core, and a hard edge reads as paper.
    // The shape noise the march carves with takes about a third of the
    // covered area back, which is what the flat numbers here stand in for.
    thick = mix(cov * 0.55, smoothstep(0.02, 0.55, carve), sharp);
    a = mix(cov * 0.7, smoothstep(0.02, 0.30, carve) * 0.85, sharp);
    sharpOut = sharp;
  }

  void main() {
    float thick, a;
    // How well this pixel resolves the field: the relief shading below is
    // only meaningful while it does.
    float sharp = 1.0;
    // Relief slope along the deck's axes (east, south), in metres of cloud
    // top per metre of ground.
    vec2 slope = vec2(0.0);
    if (uVolWeather > 0.5 && uCirrus < 0.5) {
      volWeather(thick, a, sharp, slope);
    } else {
      // Regional coverage is authoritative. Local detail erodes a cloud
      // bank; it cannot scatter puffs across a dry region.
      vec3 regional = texture2D(uMap, vUv - vec2(uMapShift, 0.0)).rgb;
      float weather = regional.r;
      float bias = mix(-0.16, 0.20, weather);

      // Shape, in real metres, at cloud scale rather than weather scale. A
      // domain warp breaks the cells into irregular, clustered shapes of mixed
      // size; unwarped value noise reads as evenly spaced blobs (a leopard
      // print) from above.
      vec2 q = vLocal.xz / uDetail + vec2(uTime * 0.004, uTime * 0.0022);
      float fw = length(fwidth(q));
      vec2 warp = vec2(fbm(q * 0.31 + 1.7, fw * 0.31), fbm(q * 0.31 + 9.2, fw * 0.31)) - 0.5;
      q += warp * mix(2.2, 0.8, uCirrus);
      float base = fbm(q, fw);
      float billow = fbm(q * 3.1 + 5.0, fw * 3.1);
      float shaped = mix(
        base + 0.38 * billow - 0.52 + bias,                       // cumulus: lumpy
        base * 0.72 + 0.30 * fbm(vec2(q.x * 0.3, q.y * 2.4), fw * 2.4) - 0.44 + bias, // cirrus: stretched
        uCirrus
      );
      // Where even whole clouds are smaller than a pixel, the shape noise has
      // averaged out to nothing; let the weather map say how cloudy it is there.
      float detailW = 1.0 - smoothstep(0.35, 1.4, fw);
      sharp = detailW;
      shaped = mix(uCoverage + (weather - 0.42) * 0.9, shaped, detailW);
      // Treat the field as thickness rather than a mask. A flat cutout reads as
      // paper; carrying thickness through to both opacity and shading gives the
      // deck the suggestion of depth a single shell cannot otherwise have.
      thick = smoothstep(uCoverage, uCoverage + mix(0.34, 0.44, uCirrus), shaped);
      a = smoothstep(uCoverage, uCoverage + mix(0.12, 0.22, uCirrus), shaped);
      float regionalGate = smoothstep(0.035, 0.20, weather);
      float bank = smoothstep(0.12, 0.52, weather) * (1.0 - regional.g) * (1.0 - uCirrus);
      thick = max(thick, bank * 0.7) * regionalGate;
      a = max(a, bank * 0.95) * regionalGate;
    }

    // Fade out as the camera crosses the deck, so flying through it is a
    // dissolve rather than a clipped plane.
    float cross = clamp(abs(uCamAlt - uDeckAlt) / uThickness, 0.0, 1.0);
    a *= cross * cross;
    a *= vRim * uOpacity * uHighFade;

    // Lighting. Tops catch the sun, bases sit in their own shadow, and a rim
    // of brightness survives at the edges where the cloud is thin.
    float up = uSunDir.y;
    float lit = clamp(0.35 + 0.65 * up, 0.0, 1.0);
    float fromBelow = step(uCamAlt, uDeckAlt);
    vec3 top = vec3(1.02, 1.01, 0.99);
    // Seen from above, cumulus tops are domes: sunlit on one side, in their
    // own shade on the other. A single shell has no relief, so take it from
    // the thickness field's slope (screen derivatives solved back into the
    // deck plane) and light that surface. Cirrus stays flat.
    if (uVolWeather < 0.5 || uCirrus > 0.5) {
      // The deck's own noise has no field to read a slope off, so it still
      // solves one from the screen derivatives, bounded.
      vec2 jx = dFdx(vLocal.xz), jy = dFdy(vLocal.xz);
      float det = jx.x * jy.y - jx.y * jy.x;
      vec2 grad = abs(det) > 1e-9
        ? vec2(dFdx(thick) * jy.y - dFdy(thick) * jx.y, dFdy(thick) * jx.x - dFdx(thick) * jy.x) / det
        : vec2(0.0);
      slope = grad * (uThickness * 0.9);
    }
    vec3 nrm = normalize(vec3(-clamp(slope.x, -2.0, 2.0), 1.0, -clamp(slope.y, -2.0, 2.0)));
    float relief = (1.0 - fromBelow) * (1.0 - uCirrus) * sharp;
    float sunSide = max(dot(nrm, uSunDir), 0.0);
    top *= mix(1.0, 0.52 + 0.62 * sunSide, relief);
    // Not called "base": that name is already the noise field above, and the
    // redefinition failed the whole shader, so the decks never drew at all.
    vec3 underside = vec3(0.46, 0.50, 0.58);
    // Thin edges let light through and stay bright from either side; thick
    // centres are the part that goes dark underneath.
    float shade = fromBelow * mix(0.80, 0.35, uCirrus) * thick;
    vec3 col = mix(top, underside, shade);
    // A little extra brightness right at the wisps, which is where a real
    // cloud is most translucent.
    col = mix(col, top * 1.06, (1.0 - thick) * 0.45);
    // Night cloud is dark: without the sun there is only moon and starlight.
    col *= mix(0.28, 1.0, lit) * mix(0.05, 1.0, uDayLight);
    // Warm the edges near sunrise and sunset.
    col = mix(col, col * vec3(1.25, 0.86, 0.66), clamp(1.0 - abs(up) * 4.0, 0.0, 1.0) * uDayLight);
    // Aerial perspective along the actual view ray: the same exponential
    // atmosphere integral the terrain's fog uses, from the camera's altitude
    // to the deck's, so a deck seen from above at the horizon hazes over
    // while the one right below stays crisp.
    const float H = 2000.0;
    float dh = uDeckAlt - uCamAlt;
    float e0 = exp(-max(uCamAlt, 0.0) / H), e1 = exp(-max(uDeckAlt, 0.0) / H);
    float tau = abs(dh) > 1.0 ? uHazeBeta * vDist * H * (e0 - e1) / dh : uHazeBeta * vDist * e0;
    // Far out the ray skims the low air over the curved ground (airdepth.js).
    if (vDist > 30000.0) {
      float muD = (uDeckAlt - uCamAlt - vDist * vDist / 12742000.0) / vDist;
      tau = mix(tau, uHazeBeta * airCurvedDepth(max(uCamAlt, 0.0), muD, vDist, H), smoothstep(30000.0, 60000.0, vDist));
    }
    float haze = 1.0 - exp(-max(tau, 0.0));
    col = mix(col, uHazeCol, haze);
    // Far out, a deck this thin is all foreshortening; let it thin away.
    a *= 1.0 - smoothstep(60000.0, 220000.0, vDist) * 0.8;
    // Near the camera the volumetric clouds (volclouds.js) replace this
    // deck; it comes in across the same band the volume fades out over.
    a *= mix(1.0, smoothstep(uVolFade.x, uVolFade.y, vDist), uVolAmount);

    // A shell vertex that comes out non-finite (or a degenerate triangle,
    // whose interpolation divides by a zero area) spreads NaN across every
    // triangle that touches it, and the hardware wrote those as hard black
    // shards over the sea from 70 km up. A comparison is false for NaN, so
    // this drops them: anything that is not a number contributes nothing.
    // A value that is not a number anywhere in the chain above used to reach
    // the frame as a hard black shard over the sea from 70 km up (the deck
    // covers every pixel there, so there was nothing to hide it). A
    // comparison is false for NaN, so this drops those fragments: what is
    // not a number contributes nothing.
    if (!(a > 0.0) || !(col.r >= 0.0) || !(col.g >= 0.0) || !(col.b >= 0.0)) discard;
    gl_FragColor = vec4(col * a, a);
  }
`;

// One curved shell. Mirrors buildRing in terrain.js but at a fixed altitude
// with no terrain displacement.
function buildShell(R, basis, altitude, halfSize, cells) {
  const n = cells + 1;
  const positions = new Float32Array(n * n * 3);
  const uvs = new Float32Array(n * n * 2);
  const rims = new Float32Array(n * n);
  const indices = [];
  const dir = new THREE.Vector3();
  const p = new THREE.Vector3();
  const centre = basis.up.clone().multiplyScalar(R + altitude);
  for (let j = 0; j < n; j++) {
    for (let i = 0; i < n; i++) {
      const x = -halfSize + (2 * halfSize * i) / cells;
      const z = -halfSize + (2 * halfSize * j) / cells;
      dirAt(basis, R, x, z, dir);
      p.copy(dir).multiplyScalar(R + altitude).sub(centre);
      const k = j * n + i;
      positions[k * 3] = p.dot(basis.east);
      positions[k * 3 + 1] = p.dot(basis.up);
      positions[k * 3 + 2] = -p.dot(basis.north);
      const lat = Math.asin(THREE.MathUtils.clamp(dir.y, -1, 1));
      const lon = Math.atan2(-dir.z, dir.x);
      uvs[k * 2] = THREE.MathUtils.euclideanModulo(lon / TWO_PI + 0.5, 1);
      uvs[k * 2 + 1] = 0.5 + lat / Math.PI;
      const rr = Math.max(Math.abs(x), Math.abs(z)) / halfSize;
      const t = THREE.MathUtils.clamp((rr - 0.55) / 0.45, 0, 1);
      rims[k] = 1 - t * t * (3 - 2 * t);
    }
  }
  for (let j = 0; j < cells; j++) {
    for (let i = 0; i < cells; i++) {
      const a = j * n + i, b = a + 1, c = a + n, d = c + 1;
      indices.push(a, c, b, b, c, d);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  g.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  g.setAttribute('aRim', new THREE.BufferAttribute(rims, 1));
  g.setIndex(indices);
  g.computeBoundingSphere();
  return g;
}

const LAYERS = [
  // Detail scale is metres per noise cell, so it sets apparent cloud size.
  // Fair-weather cumulus run a few hundred metres across; the previous 5.2 km
  // meant you never saw more than a cell or two and it read as a gradient.
  // altitude m, half-size m, cells, coverage, detail m, opacity, cirrus, fade band m
  { alt: 2100, half: 160000, cells: 128, coverage: 0.0, detail: 700, opacity: 0.92, cirrus: 0, thickness: 700 },
  { alt: 9200, half: 300000, cells: 96, coverage: 0.0, detail: 4200, opacity: 0.45, cirrus: 1, thickness: 2200 }
];

export class CloudDecks {
  // centreH: the terrain patch's centre elevation. The decks are children of
  // that patch, whose origin sits on the ground, while each shell is built
  // centred on its own altitude and therefore lands at local y = 0. Without
  // lifting each mesh by (altitude - centreH) both decks end up buried in the
  // terrain, which is exactly as visible as having no clouds at all.
  constructor(R, latDeg, lonDeg, texture, centreH = 0) {
    this.R = R;
    this.lat = latDeg * Math.PI / 180;
    this.basis = siteBasis(latDeg * Math.PI / 180, lonDeg * Math.PI / 180);
    this.group = new THREE.Group();
    this.group.name = 'cloudDecks';
    this.layers = [];
    for (const L of LAYERS) {
      const mat = new THREE.ShaderMaterial({
        vertexShader: VERT,
        fragmentShader: FRAG,
        uniforms: {
          uMap: { value: texture },
          uSunDir: { value: new THREE.Vector3(0, 1, 0) },
          uSkyCol: { value: new THREE.Color(0.5, 0.6, 0.8) },
          uTime: { value: 0 },
          uCamAlt: { value: 0 },
          uDeckAlt: { value: L.alt },
          uThickness: { value: L.thickness },
          uCoverage: { value: L.coverage },
          uDetail: { value: L.detail },
          uOpacity: { value: L.opacity },
          uDayLight: { value: 1 },
          uCirrus: { value: L.cirrus },
          uHighFade: { value: 1 },
          uHazeCol: { value: new THREE.Color(0.6, 0.7, 0.85) },
          uHazeBeta: { value: 2.2e-5 },
          uVolFade: { value: new THREE.Vector2(30000, 50000) },
          uVolAmount: { value: 0 },
          uVolWeather: { value: 0 },
          uWeather: { value: null },
          uWeatherOff: { value: new THREE.Vector3() },
          uTiles: { value: new THREE.Vector3(1, 1, 1) },
          uUp: { value: new THREE.Vector3(0, 1, 0) },
          uShellH: { value: 0 },
          uCloud: { value: new THREE.Vector4() },
          uCloud2: { value: new THREE.Vector4() },
          uToSurf: { value: new THREE.Matrix3() },
          uWindT: { value: new THREE.Vector3(1, 0, 0) },
          uWindB: { value: new THREE.Vector3(0, 0, 1) },
          uCamR: { value: 6371000 },
          uField: { value: new THREE.Vector2(1, 0) },
          uPixelAngle: { value: 0.001 },
          uMapUV: { value: new THREE.Vector2() },
          uMapShift: { value: 0 },
          uDuDp: { value: new THREE.Vector3() },
          uDvDp: { value: new THREE.Vector3() }
        },
        transparent: true,
        depthWrite: false,
        side: THREE.DoubleSide,
        extensions: { derivatives: true },
        blending: THREE.CustomBlending,
        blendSrc: THREE.OneFactor,
        blendDst: THREE.OneMinusSrcAlphaFactor,
        premultipliedAlpha: true,
        toneMapped: true
      });
      const mesh = new THREE.Mesh(buildShell(R, this.basis, L.alt, L.half, L.cells), mat);
      mesh.position.y = L.alt - centreH;
      mesh.frustumCulled = false;
      // Under the terrain's opaque rings but over the sky dome.
      mesh.renderOrder = 2;
      this.group.add(mesh);
      this.layers.push({ mesh, mat, def: L });
    }
  }

  // sunDirLocal: sun direction in the deck's local (east, up, south) frame.
  // hazeColor: the lit low-air colour haze fades toward (defaults to skyColor).
  update(camAltitude, sunDirLocal, skyColor, dayLight, time, hazeColor = null) {
    for (const l of this.layers) {
      const u = l.mat.uniforms;
      u.uCamAlt.value = camAltitude;
      u.uSunDir.value.copy(sunDirLocal);
      u.uSkyCol.value.copy(skyColor);
      u.uHazeCol.value.copy(hazeColor || skyColor);
      u.uDayLight.value = dayLight;
      u.uTime.value = time;
      // From high up the app's own cloud layer on the planet already shows
      // this weather at the right scale; the local decks hand over to it, so
      // there is never a doubled layer or a shell of sub-pixel noise.
      const high = 1 - THREE.MathUtils.smoothstep(camAltitude, DECK_HIGH_FADE[0], DECK_HIGH_FADE[1]);
      u.uHighFade.value = high;
      // While the volumes own the cumulus (setVolumeHandoff) this flat deck
      // stays hidden all the way to the orbital handoff; drawn under the
      // volumes from tens of kilometres up it read as a blotchy second layer.
      l.mesh.visible = high > 0.001 && !(this.volumeOwned && !l.def.cirrus);
    }
  }

  // Hand the near field to the volumetric clouds: amount 0..1 (0 when they
  // are off), and the distance band (m) over which the cumulus deck fades
  // back in. The cirrus veil sits far above the volume and is untouched.
  setVolumeHandoff(amount, near, far, fullCoverage = false) {
    this.volumeOwned = !!fullCoverage;
    for (const l of this.layers) {
      const u = l.mat.uniforms;
      u.uVolAmount.value = l.def.cirrus ? 0 : amount;
      // The volume now covers the visible horizon. Its altitude fade blends
      // directly into orbital weather, not into this flat approximation.
      if (!l.def.cirrus && fullCoverage) l.mesh.visible = false;
      u.uVolFade.value.set(near, far);
    }
  }

  // The cloud map's wind drift at the patch for the globe's weather time
  // (src/earthweather.js), so the decks read the map where the globe and the
  // orbital shell read it. volclouds.js sets it every Earth frame.
  setMapShift(weatherTime) {
    const shift = earthWeatherDriftCPU(this.lat, weatherTime);
    for (const l of this.layers) l.mat.uniforms.uMapShift.value = shift;
  }

  // Share the volumetric clouds' weather uniforms (volclouds.js), or pass
  // null to go back to the deck's own noise.
  setWeather(shared) {
    for (const l of this.layers) {
      const u = l.mat.uniforms;
      if (!shared) { u.uVolWeather.value = 0; continue; }
      for (const k of ['uWeather', 'uWeatherOff', 'uTiles', 'uUp', 'uShellH', 'uCloud', 'uCloud2', 'uToSurf', 'uWindT', 'uWindB', 'uCamR', 'uField', 'uPixelAngle', 'uMapUV', 'uDuDp', 'uDvDp']) u[k] = shared[k];
      u.uVolWeather.value = 1;
    }
  }

  setVisible(v) { this.group.visible = v; }

  dispose() {
    for (const l of this.layers) { l.mesh.geometry.dispose(); l.mat.dispose(); }
    this.layers.length = 0;
  }
}
